import {
  getAuth,
  signInWithEmailAndPassword,
  setPersistence,
  browserSessionPersistence,
  reauthenticateWithCredential,
  EmailAuthProvider,
  GoogleAuthProvider,
  FacebookAuthProvider,
  OAuthProvider,
  signInWithPopup,
  getAdditionalUserInfo,
  SAMLAuthProvider,
} from "firebase/auth";
import {
  getDatabase, ref as dbRef, set, goOffline, goOnline,
} from "firebase/database";
import { i18n } from "../../i18n";
import getAuthenticatedUser from "./getAuthenticatedUser";
import notificationsStore, { NotificationTypes } from "../../stores/notifications";
import sendRegistrationMail from "./sendRegistrationMail";
import sanitizeObject from "../utils/sanitizeObject";
import { correctUsersRoute } from "../../constants/dbRoutes";
import addNewUserCredits from "./addNewUserCredits";
import errorFeatureIds from "../../constants/errorFeatureIds";
import { handleErrorTracking } from "../../middleWare/errorTracker";
import { skodaOidcConfig } from "../../constants/skodaOidcConfig";
import handleSkodaUserLogin from "./handleSkodaUserLogin";

export const providers = {
  google: "google",
  facebook: "facebook",
  apple: "apple",
  email: "email",
  microsoft: "microsoft",
  skoda: "skoda", // Add Skoda OIDC provider
};

const providersMap = {
  google: GoogleAuthProvider,
  facebook: FacebookAuthProvider,
  apple: OAuthProvider,
  email: EmailAuthProvider,
  microsoft: SAMLAuthProvider,
  skoda: OAuthProvider, // Skoda uses OIDC which extends OAuth
};

const createConstructorInstance = (provider) => {
  if (provider === providers.microsoft) {
    return new providersMap[provider]("saml.microsoft-azure-ad");
  } if (provider === providers.skoda) {
    const skodaProvider = new providersMap[provider](skodaOidcConfig.providerId);

    // Add scopes
    skodaOidcConfig.scopes.forEach((scope) => {
      skodaProvider.addScope(scope);
    });

    // Set custom parameters
    skodaProvider.setCustomParameters(skodaOidcConfig.customParameters);

    return skodaProvider;
  }

  return new providersMap[provider]();
};

export default handleErrorTracking(
  async (
    loginEmail,
    loginPassword,
    provider = providers.email,
  ) => {
    const userLoggingInWithEmail = provider === providers.email;
    const notificationsStoreInstance = notificationsStore();
    const auth = getAuth();
    const providerInstance = userLoggingInWithEmail ? providersMap[provider] : createConstructorInstance(provider);

    await setPersistence(auth, browserSessionPersistence);

    const userCredentials = await (userLoggingInWithEmail ? signInWithEmailAndPassword(
      auth,
      loginEmail,
      loginPassword,
    ) : signInWithPopup(auth, providerInstance));
    const {
      user: {
        displayName, email, uid, // emailVerified,
      },
    } = userCredentials;

    const { isNewUser, profile } = getAdditionalUserInfo(userCredentials);

    // Handle Skoda OIDC login separately - uses dedicated backend function
    if (provider === providers.skoda) {
      try {
        // Extract claims from the raw OIDC ID token (azp, roles are stripped by Firebase from profile)
        let tokenClaims = {};
        const oauthCredential = OAuthProvider.credentialFromResult(userCredentials);

        if (oauthCredential?.idToken) {
          try {
            const tokenParts = oauthCredential.idToken.split(".");
            const decodedPayload = JSON.parse(atob(tokenParts[1]));
            tokenClaims = {
              azp: decodedPayload.azp || "",
              roles: decodedPayload.roles || [],
            };
          } catch (decodeError) {
            console.warn("[Skoda Login] Token decode error:", decodeError);
          }
        }

        // Store the token first so the API call can use it
        const token = await userCredentials.user.getIdToken();
        window.sessionStorage.setItem("token", token);

        // Call backend to handle Skoda user (create/update + org/group assignment)
        const skodaResult = await handleSkodaUserLogin(uid, profile, tokenClaims);

        if (skodaResult.success) {
          // Force-refresh the Firebase Auth token to pick up custom claims
          // (organizationId, isSkodaUser) set by the backend via setCustomUserClaims
          const refreshedToken = await userCredentials.user.getIdToken(true);
          window.sessionStorage.setItem("token", refreshedToken);

          // Force Realtime Database to reconnect with the new token
          // (its WebSocket caches the old auth state and won't pick up new claims otherwise)
          const database = getDatabase();
          goOffline(database);
          goOnline(database);

          if (skodaResult.isNewUser) {
            notificationsStoreInstance.addNotification({
              title: i18n.global.t("views.logIn.messages.registrationOrganizationSuccess"),
              type: NotificationTypes.Success,
            });
          }

          // Return with Skoda-specific data
          return {
            displayName: profile?.name || profile?.display_name || displayName,
            email: profile?.email || email,
            uid: userCredentials.user.uid,
            isSkodaUser: true,
            organizationId: skodaResult.organizationId,
          };
        }
        // If Skoda handling failed, continue with normal flow as fallback
      } catch (skodaError) {
        // Continue with standard flow as fallback
        notificationsStoreInstance.addNotification({
          title: "Skoda login processing failed",
          type: NotificationTypes.Warning,
        });

        return null;
      }
    }

    if (isNewUser) {
      // Extract additional data from OIDC profile if available (e.g., Skoda)
      const additionalData = {};

      if (provider === providers.skoda && profile) {
        if (profile.display_name) additionalData.displayName = profile.display_name;
        if (profile.preferred_username) additionalData.preferredUsername = profile.preferred_username;
      }

      const data = sanitizeObject({
        email: email || "",
        firstName: displayName || profile?.given_name || "",
        lastName: profile?.family_name || "",
        id: uid,
        createdAt: new Date().toISOString(),
        ...additionalData,
      });

      const db = getDatabase();

      await set(dbRef(db, `${correctUsersRoute}/${uid}`), data);
      await sendRegistrationMail({
        user: data,
        language: i18n.global.locale.value,
      });

      // TODO rework this
      addNewUserCredits(uid);

      notificationsStoreInstance.addNotification({
        title: i18n.global.t("views.logIn.messages.registrationSuccess"),
        type: NotificationTypes.Success,
      });
    }

    if (userLoggingInWithEmail && userCredentials.user) {
      // Re-authenticate user every 1 hour
      setInterval(async () => {
        const credential = providerInstance.credential(
          loginEmail,
          loginPassword,
        );
        const { user: reauthenticatedUser } = await reauthenticateWithCredential(getAuthenticatedUser(), credential);

        if (reauthenticatedUser) {
          reauthenticatedUser.getIdToken(true).then((token) => {
            window.sessionStorage.setItem("token", token);
          });
        } else {
          window.location.reload();
        }
      }, 3600000);
    }

    return {
      displayName,
      email,
      uid: userCredentials.user.uid,
    };
  },
  {
    feature: errorFeatureIds.auth.methods.logInUser,
    measurePerformance: true,
    notification: {
      isFirebaseError: true,
    },
  },
);
